import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Monitor, ArrowRight, ShieldCheck, Lightning, Cursor, ChatCircleDots, LockKey, GlobeHemisphereWest } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const HERO_BG = "https://images.unsplash.com/photo-1771011726574-60ab6028724a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMHRlY2glMjBncmlkJTIwYmx1ZXxlbnwwfHx8fDE3ODYzODE5MDZ8MA&ixlib=rb-4.1.0&q=85";

function Feature({ icon: Icon, title, desc, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="border border-zinc-800 bg-[#121214] p-8 hover:border-[#0055FF] transition-colors group"
      data-testid={`feature-card-${index}`}
    >
      <Icon size={32} weight="duotone" className="text-[#00E5FF] mb-6 group-hover:text-[#0055FF] transition-colors" />
      <h3 className="font-heading font-black text-xl tracking-tight mb-2">{title}</h3>
      <p className="text-zinc-400 text-sm leading-relaxed">{desc}</p>
    </motion.div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const go = () => navigate(user ? "/dashboard" : "/login");

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white relative overflow-hidden">
      {/* Nav */}
      <nav className="relative z-20 flex items-center justify-between px-6 md:px-12 py-6 border-b border-zinc-900">
        <div className="flex items-center gap-2" data-testid="brand-logo">
          <Monitor size={26} weight="duotone" className="text-[#0055FF]" />
          <span className="font-heading font-black text-lg tracking-tighter">NEXUS<span className="text-[#0055FF]">RDP</span></span>
        </div>
        <button
          onClick={go}
          data-testid="nav-login-btn"
          className="font-mono text-xs uppercase tracking-widest bg-white text-black px-5 py-2.5 hover:bg-zinc-200 transition-colors"
        >
          {user ? "Dashboard" : "Sign In"}
        </button>
      </nav>

      {/* Hero */}
      <section className="relative scanlines">
        <div
          className="absolute inset-0 tech-grid opacity-60"
          style={{ maskImage: "radial-gradient(circle at 30% 20%, black, transparent 70%)" }}
        />
        <img src={HERO_BG} alt="" className="absolute right-0 top-0 w-[55%] h-full object-cover opacity-10 mix-blend-screen pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 border border-zinc-800 px-3 py-1.5 mb-8"
            >
              <span className="w-1.5 h-1.5 bg-[#34C759] animate-pulse" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">Low-latency · Encrypted P2P</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
              className="font-heading font-black text-5xl md:text-7xl tracking-tighter leading-[0.95]"
            >
              CONTROL ANY<br />SCREEN.<span className="text-[#0055FF]"> ANYWHERE.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
              className="text-zinc-400 text-lg mt-6 max-w-md leading-relaxed"
            >
              Instant remote desktop access over a secure peer-to-peer channel. Share your screen, take control, and collaborate in real time — no downloads required.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap gap-4 mt-10"
            >
              <button
                onClick={go}
                data-testid="hero-start-btn"
                className="group inline-flex items-center gap-2 bg-[#0055FF] text-white font-mono text-sm uppercase tracking-widest px-7 py-4 hover:bg-blue-700 transition-colors"
              >
                Launch Console
                <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </button>
              <div className="flex items-center gap-2 text-zinc-500 font-mono text-xs">
                <LockKey size={16} /> End-to-end WebRTC
              </div>
            </motion.div>
          </div>

          {/* Mock terminal card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
            className="hidden md:block"
          >
            <div className="border border-zinc-800 bg-[#121214] shadow-2xl">
              <div className="flex items-center gap-2 border-b border-zinc-800 px-4 py-3">
                <span className="w-3 h-3 rounded-full bg-[#FF3B30]" />
                <span className="w-3 h-3 rounded-full bg-[#FFCC00]" />
                <span className="w-3 h-3 rounded-full bg-[#34C759]" />
                <span className="font-mono text-xs text-zinc-500 ml-3">session — active</span>
              </div>
              <div className="p-6 font-mono text-sm space-y-2">
                <p className="text-zinc-500">$ connect --id 708 416 916</p>
                <p className="text-[#00E5FF]">→ handshaking peer...</p>
                <p className="text-[#34C759]">→ secure channel established ✓</p>
                <p className="text-zinc-400">→ streaming 1920×1080 @ 60fps</p>
                <div className="border border-zinc-800 mt-4 aspect-video flex items-center justify-center bg-black/40">
                  <Monitor size={48} weight="thin" className="text-zinc-700" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 border-t border-zinc-900">
        <h2 className="font-heading font-light text-3xl md:text-4xl tracking-tighter mb-4">
          Engineered for <span className="font-black">absolute control</span>.
        </h2>
        <p className="text-zinc-500 max-w-lg mb-14">Everything you need to operate a machine remotely, wrapped in a fast, secure console.</p>

        <div className="grid md:grid-cols-3 gap-4">
          <Feature index={0} icon={Cursor} title="Full Remote Control" desc="Take over the mouse and keyboard of any connected screen with live cursor mirroring." />
          <Feature index={1} icon={Lightning} title="Peer-to-Peer Speed" desc="Direct WebRTC streaming keeps latency to a minimum — it feels like you're right there." />
          <Feature index={2} icon={ShieldCheck} title="ID + PIN Access" desc="Every session is gated by a unique 9-digit ID and rotating PIN. No open doors." />
          <Feature index={3} icon={ChatCircleDots} title="In-Session Chat" desc="Talk to the other side without leaving the session. Messages sync instantly." />
          <Feature index={4} icon={GlobeHemisphereWest} title="Device Directory" desc="Save your machines, see who's online, and reconnect with a single click." />
          <Feature index={5} icon={LockKey} title="Encrypted Sessions" desc="Login with email or Google. Sessions are token-secured and expire automatically." />
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter">Ready to connect?</h2>
          <button
            onClick={go}
            data-testid="cta-start-btn"
            className="inline-flex items-center gap-2 bg-white text-black font-mono text-sm uppercase tracking-widest px-8 py-4 hover:bg-zinc-200 transition-colors"
          >
            Get Started <ArrowRight size={18} weight="bold" />
          </button>
        </div>
        <div className="border-t border-zinc-900 py-6 text-center font-mono text-xs text-zinc-600">
          NEXUSRDP © 2026 · Built for the remote-first world
        </div>
      </section>
    </div>
  );
}
